import React, { useState, useEffect } from "react";
import Swal from "sweetalert2";
import {
  collection,
  getDocs,
  serverTimestamp,
  updateDoc,
  setDoc,
  doc,
  addDoc,
  onSnapshot,
} from "firebase/firestore";
import { v4 as uuidv4 } from "uuid";
import { db } from "../firebaseConfig";
import TablaCotizaciones from "./TablaCotizaciones";
import Nav from "./Nav";
import { useNavContext } from "../context/NavContext";
import Sidebar from "./Sidebar";
import moment from "moment";
import DatePicker, { registerLocale } from "react-datepicker";
import es from "date-fns/locale/es";
import "react-datepicker/dist/react-datepicker.css";
import VendedorModal from "./VendedorModal";
import { useNumeroConfig } from "../hooks/useNumeroConfig";
import useArchivos from "../hooks/useArchivos";
import useProductos from "../hooks/useProductos";

const SPELL = Object.freeze({
  spellCheck: true,
  lang: "es",
  autoCorrect: "on",
  autoCapitalize: "sentences",
});

// --- Helper: normaliza el id del cliente que venga en la cotización ---
const getClienteIdFromCotizacion = (c) => {
  if (!c) return "";
  const v = c.cliente;

  // si ya es string (id)
  if (typeof v === "string") return v.trim();

  // si viene como DocumentReference (tiene .id)
  if (v && typeof v === "object" && typeof v.id === "string") return v.id;

  // sin id; se resolverá por nombre en el efecto de reconciliación
  return "";
};

const norm = (s) => (s || "").trim().toLowerCase();

// --- Helper: separar titulo/descripcion una sola vez ---
const normalizaProducto = (p = {}) => {
  // Si ya traen título explícito, respétalo y usa la descripción tal cual
  if (typeof p.titulo === "string" && p.titulo.trim() !== "") {
    return {
      titulo: p.titulo,
      descripcion: String(p.descripcion || "").replace(/\r/g, ""),
    };
  }

  // Si no hay título, lo sacamos de la PRIMERA línea y el resto es cuerpo
  const base = String(p.descripcion ?? p.nombre ?? "").replace(/\r/g, "");
  const [primera = "", ...resto] = base.split("\n");
  return {
    titulo: primera.trim(),
    descripcion: resto.join("\n").trim(),
  };
};

moment.locale("es");
registerLocale("es", es);

function MiniSelect({ value, onChange, options, width = 64, height = 40 }) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);

  React.useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div ref={ref} style={{ position: "relative", width, height }}>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        style={{
          width: "100%",
          height: "100%",
          border: "none",
          borderLeft: "1px solid #ddd",
          background: "#fff",
          fontSize: 14,
          fontWeight: 500,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderTopRightRadius: 8,
          borderBottomRightRadius: 8,
          boxShadow: "inset 0 0 0 1px #ddd",
          color: "#111",
          padding: 0,
        }}
      >
        <span style={{ position: "relative", top: "1px" }}>{value}</span>
        <i
          className="bi bi-caret-down-fill"
          style={{
            marginLeft: 6,
            fontSize: 12,
            color: "#666",
            position: "relative",
            top: "1px",
          }}
        />
      </button>

      {open && (
        <div
          role="listbox"
          style={{
            position: "absolute",
            top: "calc(100% + 8px)",
            right: 0,
            zIndex: 1000,
            minWidth: 100,
            background: "#fff",
            border: "1px solid #e7e7ea",
            borderRadius: 12,
            boxShadow: "0 8px 24px rgba(0,0,0,.12)",
            padding: 6,
          }}
        >
          {options.map((opt) => {
            const selected = value === opt.value;
            return (
              <div
                key={opt.value}
                role="option"
                aria-selected={selected}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  onChange(opt.value);
                  setOpen(false);
                }}
                style={{
                  padding: "8px 12px",
                  borderRadius: 10,
                  fontSize: 14,
                  textAlign: "center",
                  cursor: "pointer",
                  background: selected ? "#eaf1ff" : "transparent",
                  color: selected ? "#0a66ff" : "#111",
                  transition: "background 0.15s",
                }}
              >
                {opt.label}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function CotizacionForm(props) {
  const { cotizacion, cotizaciones, onFormDirtyChange, closeModal } = props;
  const {
    numero: numeroCotizacionAuto,
    modo: modoNumeroCotizacion,
    abrirConfiguracion,
    NumeroConfigModalUI,
  } = useNumeroConfig("numeroCotizacion", {
    title: "Configurar número de cotización",
  });
  const {
    archivos,
    cargando,
    initArchivos,
    manejarArchivos,
    eliminarArchivo,
    descargarArchivo,
    obtenerIcono,
  } = useArchivos("cotizaciones");
  const [productos, setProductos] = useState([]);
  const {
    datos,
    descuento,
    añadirFila,
    eliminar,
    handleInputChange,
    filteredProductos,
    activeRow,
    total,
    obtenerIVA,
    calcularSubtotal,
    setDatos,
    setFilteredProductos,
    agregarBulk,
    setDescuento,
  } = useProductos(
    cotizacion?.productosSeleccionados || [],
    cotizacion?.descuento || 0,
    productos
  );
  const [numeroSobrescrito, setNumeroSobrescrito] = useState(null);
  const [cliente, setCliente] = useState("");
  const [clientes, setClientes] = useState([]);
  const [asunto, setAsunto] = useState("");
  const [fechaCotizacion, setFechaCotizacion] = useState(new Date());
  const [fechaVencimiento, setFechaVencimiento] = useState(null);
  const [atencion, setAtencion] = useState("");
  const [planta, setPlanta] = useState("");
  const [divisa, setDivisa] = useState("MXN");
  const [area, setArea] = useState("");
  const [referencia, setReferencia] = useState("");
  const [notas, setNotas] = useState("");
  const [vendedores, setVendedores] = useState([]);
  const [vendedorNombre, setVendedorNombre] = useState("");
  const [vendedorCorreo, setVendedorCorreo] = useState("");
  const [modalVendedorIsOpen, setModalVendedorIsOpen] = useState(false);
  const [mostrarTabla] = useState(false);
  const [modoEdicion, setModoEdicion] = useState(false);
  const { proximasAVencer, proximosEventos } = useNavContext();
  const [formDirty, setFormDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [incoterm, setIncoterm] = useState("");
  const [tiempoEntrega, setTiempoEntrega] = useState("");
  const [condicionesPago, setCondicionesPago] = useState("");
  const [tipoDescuentoGlobal, setTipoDescuentoGlobal] = useState("%");

  useEffect(() => {
    if (cotizacion) {
      setModoEdicion(true);

      // Inicializar archivos
      initArchivos(cotizacion.archivos || []);

      // Campos básicos
      setCliente(getClienteIdFromCotizacion(cotizacion));
      setReferencia(cotizacion.referencia || "");
      setFechaCotizacion(
        cotizacion.fechaCotizacion
          ? new Date(cotizacion.fechaCotizacion)
          : new Date()
      );
      setFechaVencimiento(
        cotizacion.fechaVencimiento
          ? new Date(cotizacion.fechaVencimiento)
          : null
      );
      setVendedorNombre(cotizacion.vendedorNombre || "");
      setVendedorCorreo(cotizacion.vendedorCorreo || "");

      // >>> NUEVO
      setIncoterm(cotizacion.incoterm || "");
      setTiempoEntrega(cotizacion.tiempoEntrega || "");
      setCondicionesPago(cotizacion.condicionesPago || "");

      // Asegura que el vendedor exista en la lista
      setVendedores((prev) =>
        cotizacion.vendedorNombre &&
        !prev.some((v) => v.nombre === cotizacion.vendedorNombre)
          ? [
              ...prev,
              {
                nombre: cotizacion.vendedorNombre,
                correo: cotizacion.vendedorCorreo,
              },
            ]
          : prev
      );

      // Productos
      setDatos(
        (cotizacion.productosSeleccionados || []).map((p) => {
          const base = {
            id: uuidv4(),
            ...p,
            numeroDeParteCliente: p.numeroDeParteCliente || "",
            cantidad: p.cantidad ?? 1,
            tarifa: p.tarifa ?? 0,
            iva: p.iva ?? 0,
            descuento: p.descuento ?? 0,
            importe: p.importe ?? 0,
          };
          // Normaliza una SOLA vez: título y cuerpo separados
          const { titulo, descripcion } = normalizaProducto(p);
          return { ...base, titulo, descripcion };
        })
      );
      setDescuento(parseFloat(cotizacion.descuento) || 0);

      // Resto
      setAtencion(cotizacion.atencion || "");
      setPlanta(cotizacion.planta || "");
      setArea(cotizacion.area || "");
      setAsunto(cotizacion.asunto || "");
      setDivisa(cotizacion.divisa || "MXN");
      setNotas(cotizacion.notas || "");

      // Número en edición
      setNumeroSobrescrito(cotizacion.numeroCotizacion || null);
    } else {
      // Modo creación
      setModoEdicion(false);
      setNumeroSobrescrito(null);

      setDatos([
        {
          id: uuidv4(),
          titulo: "", // <- clave: empezamos separados
          descripcion: "",
          numeroDeParteCliente: "",
          cantidad: 1,
          tarifa: 0,
          iva: 0,
          descuento: 0,
          tipoDescuento: "%", // o "cantidad" si así lo deseas
          importe: 0,
        },
      ]);
      setDescuento(0);

      // >>> NUEVO: reset en creación
      setIncoterm("");
      setTiempoEntrega("");
      setCondicionesPago("");

      initArchivos([]);
    }
  }, [cotizacion, initArchivos, setDatos, setDescuento]);

  useEffect(() => {
    if (!modoEdicion || !cotizacion) return;
    if (!clientes.length) return;

    // si ya coincide, no hagas nada
    if (cliente && clientes.some((c) => c.id === cliente)) return;

    // 1) intentar casar por id guardado en la cotización (string o ref.id)
    const byId =
      typeof cotizacion.cliente === "string"
        ? clientes.find((c) => c.id === cotizacion.cliente.trim())
        : cotizacion.cliente &&
          typeof cotizacion.cliente === "object" &&
          cotizacion.cliente.id
        ? clientes.find((c) => c.id === cotizacion.cliente.id)
        : null;

    // 2) intentar por nombreCliente (empresa)
    const byNombre = cotizacion.nombreCliente
      ? clientes.find(
          (c) => (c.empresa || "").trim() === cotizacion.nombreCliente.trim()
        )
      : null;

    // 3) fallback: si guardaron el nombre dentro de `cotizacion.cliente` (en vez del id)
    const byEmpresaEnCliente =
      typeof cotizacion.cliente === "string"
        ? clientes.find(
            (c) => (c.empresa || "").trim() === cotizacion.cliente.trim()
          )
        : null;

    const match = byId || byNombre || byEmpresaEnCliente;
    if (match) setCliente(match.id);
  }, [modoEdicion, cotizacion, clientes, cliente]);

  useEffect(() => {
    // 1) Suscribirse a vendedores en tiempo real
    const unsubscribeVendedores = onSnapshot(
      collection(db, "vendedores"),
      (snap) =>
        setVendedores(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => console.error("Error al suscribirse a vendedores:", err)
    );

    // 2) Cargar clientes y productos una vez (clientes deduplicados por empresa)
    const fetchClientesYProductos = async () => {
      try {
        // Clientes
        const clientesSnap = await getDocs(collection(db, "clientes"));
        const rawClientes = clientesSnap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));

        // DEDUP por empresa (case/espacios)
        const clientesUnicos = Array.from(
          new Map(rawClientes.map((c) => [norm(c.empresa), c])).values()
        );

        setClientes(clientesUnicos);

        // Productos
        const productosSnap = await getDocs(collection(db, "productos"));
        const productosList = productosSnap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
          tipoDescuento: "cantidad",
          valorDescuento: 0,
        }));
        setProductos(productosList);
      } catch (error) {
        console.error("Error al obtener clientes o productos:", error);
      }
    };

    fetchClientesYProductos();

    // Cleanup al desmontar
    return () => {
      unsubscribeVendedores();
    };
  }, []);

  const handleCancel = () => {
    if (formDirty) {
      Swal.fire({
        title: "¿Seguro que quieres salir?",
        text: "Se perderán los cambios realizados",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Sí, salir",
        cancelButtonText: "Cancelar",
      }).then((result) => {
        if (result.isConfirmed) {
          // Cerrar el modal usando la función pasada como prop
          closeModal();
          setFormDirty(false); // Opcional: resetea el estado si es necesario
        }
      });
    } else {
      // Si no hay cambios, simplemente cierra el modal
      closeModal();
    }
  };

  const openModalVendedor = () => {
    setModalVendedorIsOpen(true);
  };

  const closeModalVendedor = () => {
    setModalVendedorIsOpen(false);
  };

  const guardar = async (e) => {
    e.preventDefault();

    // 0) Si ya está guardando, salir
    if (isSaving) return;
    setIsSaving(true);

    // 1) VALIDACIÓN MÍNIMA  >>> añade correoEnvio
    const numeroEfectivo = numeroSobrescrito ?? numeroCotizacionAuto;
    const atencionTrim = (atencion || "").trim();
    const tiempoEntregaTrim = (tiempoEntrega || "").trim();
    const condicionesPagoTrim = (condicionesPago || "").trim();

    if (
      !cliente ||
      !numeroEfectivo ||
      !fechaCotizacion ||
      !atencionTrim ||
      !tiempoEntregaTrim ||
      !condicionesPagoTrim
    ) {
      await Swal.fire({
        icon: "error",
        title: "Campos obligatorios faltantes",
        text: "Completa: Cliente, No. Cotización, Fecha, Atención, Tiempo de entrega y Condiciones de pago",
      });
      setIsSaving(false);
      return;
    }

    try {
      // 2) ¿NÚMERO DUPLICADO?
      const snap = await getDocs(collection(db, "cotizaciones"));
      const dup = snap.docs.find(
        (d) => d.data().numeroCotizacion === numeroEfectivo
      );
      if (dup && (!modoEdicion || dup.id !== cotizacion?.id)) {
        await Swal.fire({
          icon: "error",
          title: "Número duplicado",
          text: `Ya existe la cotización "${numeroEfectivo}".`,
        });
        return;
      }

      // 3) PREPARA LOS DATOS >>> incluye correoEnvio
      const clienteSel = clientes.find((c) => c.id === cliente) || {};
      const data = {
        cliente,
        numeroCotizacion: numeroEfectivo,
        nombreCliente: clienteSel.empresa || "",
        fechaCotizacion: fechaCotizacion.toISOString(),
        fechaVencimiento: fechaVencimiento?.toISOString() || null,
        vendedorNombre: vendedorNombre || "",
        vendedorCorreo: vendedorCorreo || "",
        atencion: atencionTrim,
        divisa: divisa || "MXN",
        productosSeleccionados: datos,
        total,
        descuento,
        createdAt: serverTimestamp(),
        estado: "Pendiente",
        archivos,

        // Solo se incluyen si tienen contenido
        ...(referencia ? { referencia } : {}),
        ...(incoterm ? { incoterm: incoterm.toUpperCase().trim() } : {}),
        ...(planta ? { planta } : {}),
        ...(area ? { area } : {}),
        ...(asunto ? { asunto } : {}),
        ...(notas ? { notas } : {}),

        // >>> NUEVOS CAMPOS
        tiempoEntrega: tiempoEntregaTrim,
        condicionesPago: condicionesPagoTrim,
      };

      // 4) CREATE o UPDATE
      // 4) CREATE o UPDATE (V2 por defecto en nuevas; conservar versión en edición)
      if (modoEdicion && cotizacion?.id) {
        await updateDoc(doc(db, "cotizaciones", cotizacion.id), {
          ...data,
          resumenVersion: cotizacion?.resumenVersion ?? 1,
        });
      } else {
        await addDoc(collection(db, "cotizaciones"), {
          ...data,
          resumenVersion: 2,
        });

        // Auto-incremento en modo automático
        if (modoNumeroCotizacion === "auto") {
          const match = numeroEfectivo.match(/^(.*-)?(\d+)$/);
          if (match) {
            const [, prefijo = "", raw] = match;
            const siguienteNumero = parseInt(raw, 10) + 1;
            await setDoc(
              doc(db, "configuracion", "numeroCotizacion"),
              {
                modo: "auto",
                prefijo,
                siguienteNumero,
              },
              { merge: true }
            );
          }
        }
      }

      // 5) ÉXITO
      await Swal.fire({
        icon: "success",
        title: modoEdicion ? "Cotización actualizada" : "Cotización guardada",
        showConfirmButton: false,
        timer: 1500,
      });

      // 6) RESET DIRTY + CIERRE
      setFormDirty(false);
      onFormDirtyChange?.(false);
      closeModal();
    } catch (err) {
      console.error(err);
      await Swal.fire({
        icon: "error",
        title: "Error al guardar",
        text: err.message,
      });
    } finally {
      // Siempre desbloquear
      setIsSaving(false);
    }
  };

  const clienteValue = React.useMemo(() => {
    // Si el id actual existe en la lista, úsalo
    if (cliente && clientes.some((c) => c.id === cliente)) return cliente;

    // Intentar casar por nombreCliente guardado en la cotización
    const nombreDeCotizacion = norm(cotizacion?.nombreCliente);
    if (nombreDeCotizacion) {
      const match = clientes.find(
        (c) => norm(c.empresa) === nombreDeCotizacion
      );
      if (match) return match.id;
    }

    // Fallback: si guardaron el nombre en `cotizacion.cliente` (en vez del id)
    if (typeof cotizacion?.cliente === "string" && cotizacion.cliente) {
      const match2 = clientes.find(
        (c) => norm(c.empresa) === norm(cotizacion.cliente)
      );
      if (match2) return match2.id;
    }

    return ""; // nada seleccionado
  }, [cliente, clientes, cotizacion]);

  return (
    <>
      <Nav
        proximasAVencer={proximasAVencer}
        proximosEventos={proximosEventos}
      />

      <Sidebar />

      <div className="cotizacion-form">
        <form
          lang="es"
          onKeyDown={(e) => {
            // Solo bloquear "Enter" fuera de los elementos <textarea>
            if (e.key === "Enter" && e.target.tagName !== "TEXTAREA") {
              e.preventDefault();
            }
          }}
        >
          <div>
            <div className="group apartado">
              <label htmlFor="cliente" className="required">
                Razón Social*
              </label>
              <select
                id="cliente"
                name="cliente"
                value={clienteValue}
                onChange={(e) => {
                  setCliente(e.target.value);
                  setFormDirty(true);
                }}
              >
                <option value="">Seleccionar cliente</option>
                {clientes
                  .slice()
                  .sort((a, b) =>
                    (a.empresa || "").localeCompare(b.empresa || "")
                  )
                  .map((cli) => (
                    <option key={String(cli.id)} value={String(cli.id)}>
                      {cli.empresa}
                    </option>
                  ))}
              </select>
            </div>

            <br />

            <div className="group">
              <label htmlFor="numeroCotizacion" className="required">
                No. Cotización#*
              </label>
              <div className="input-wrapper input-wrapper-expanded">
                <input
                  type="text"
                  id="numeroCotizacion"
                  name="numeroCotizacion"
                  className="numeroRemision-input"
                  // Si hay un valor manual (override), lo mostramos; si no, el automático
                  value={
                    numeroSobrescrito !== "" && numeroSobrescrito !== null
                      ? numeroSobrescrito
                      : numeroCotizacionAuto
                  }
                  onChange={(e) => {
                    // Solo permitimos override en modo manual
                    if (modoNumeroCotizacion === "manual") {
                      setNumeroSobrescrito(e.target.value);
                      setFormDirty(true);
                    }
                  }}
                  disabled={modoNumeroCotizacion === "auto"} // deshabilitado en auto
                />
                <button
                  type="button"
                  className="icon-button"
                  onClick={abrirConfiguracion}
                >
                  <i className="bi bi-gear-fill"></i>
                </button>
              </div>
            </div>

            <div className="group">
              <label htmlFor="referencia">Referencia</label>
              <input
                type="text"
                id="referencia"
                name="referencia"
                {...SPELL}
                value={referencia}
                onChange={(e) => {
                  setReferencia(e.target.value);
                  setFormDirty(true);
                }}
              />
            </div>

            <div className="group fechas">
              <div className="group">
                <label htmlFor="fecha-cotizacion" className="required">
                  Fecha de cotización*
                </label>
                <DatePicker
                  selected={fechaCotizacion}
                  onChange={(date) => {
                    setFechaCotizacion(date);
                    setFormDirty(true); // Marcar el formulario como modificado
                  }}
                  dateFormat="dd MMM yyyy"
                  placeholderText="dd MMM yyyy"
                  id="fecha-cotizacion"
                  locale="es"
                />
              </div>
              <div className="group">
                <label htmlFor="fecha-vencimiento">Fecha de vencimiento</label>
                <div>
                  <DatePicker
                    selected={fechaVencimiento}
                    onChange={(date) => {
                      setFechaVencimiento(date);
                      setFormDirty(true); // Marcar el formulario como modificado
                    }}
                    dateFormat="dd MMM yyyy"
                    placeholderText="dd MMM yyyy"
                    id="fecha-vencimiento"
                    locale="es"
                  />
                </div>
              </div>
            </div>

            <hr
              style={{
                marginBottom: "30px",
                marginTop: "20px",
                border: "1px solid #f9f9fb",
              }}
            />

            <div className="group">
              <label htmlFor="vendedorNombre">Vendedor</label>
              <select
                id="vendedorNombre"
                name="vendedorNombre"
                value={
                  vendedores.find((v) => v.nombre === vendedorNombre)?.id || ""
                }
                onChange={(e) => {
                  const selected = e.target.value;
                  if (selected === "agregar-nuevo") {
                    // abre el modal para crear uno nuevo
                    openModalVendedor();
                  } else {
                    // seleccionó uno existente
                    const v = vendedores.find((v) => v.id === selected);
                    setVendedorNombre(v?.nombre || "");
                    setVendedorCorreo(v?.correo || "");
                  }
                  setFormDirty(true);
                }}
              >
                <option value="">Seleccionar vendedor</option>
                {vendedores.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.nombre}
                  </option>
                ))}
                <option key="agregar-nuevo" value="agregar-nuevo">
                  Agregar nuevo vendedor
                </option>
              </select>

              <VendedorModal
                isOpen={modalVendedorIsOpen}
                onClose={closeModalVendedor}
                initialNombre={vendedorNombre}
                initialCorreo={vendedorCorreo}
                onSelect={(nuevo) => {
                  // callback que dispara VendedorModal al guardar/seleccionar
                  setVendedorNombre(nuevo.nombre);
                  setVendedorCorreo(nuevo.correo);
                  setFormDirty(true);
                }}
              />
            </div>

            <div className="group" id="campo-incoterm">
              <label htmlFor="incoterm">Incoterm</label>
              <select
                id="incoterm"
                name="incoterm"
                value={incoterm}
                onChange={(e) => {
                  setIncoterm(e.target.value);
                  setFormDirty(true);
                }}
              >
                <option value="">Seleccionar INCOTERM</option>
                <option value="EXW">EXW</option>
                <option value="FCA">FCA</option>
                <option value="CPT">CPT</option>
                <option value="CIP">CIP</option>
                <option value="DAP">DAP</option>
                <option value="DPU">DPU</option>
                <option value="DDP">DDP</option>
                <option value="FOB">FOB</option>
                <option value="CFR">CFR</option>
                <option value="CIF">CIF</option>
              </select>
            </div>

            <div className="group fechas">
              <div className="group">
                <label htmlFor="atencion" className="required">
                  Atención*
                </label>
                <input
                  type="text"
                  id="atencion"
                  name="atencion"
                  {...SPELL}
                  value={atencion}
                  onChange={(e) => {
                    setAtencion(e.target.value);
                    setFormDirty(true);
                  }}
                />
              </div>
              <div className="group">
                <label htmlFor="planta">Planta</label>
                <input
                  type="text"
                  id="planta"
                  name="planta"
                  {...SPELL}
                  value={planta}
                  onChange={(e) => {
                    setPlanta(e.target.value);
                    setFormDirty(true);
                  }}
                />
              </div>
            </div>

            <div className="group fechas">
              <div className="group">
                <label htmlFor="area">Área</label>
                <input
                  type="text"
                  id="area"
                  name="area"
                  {...SPELL}
                  value={area}
                  onChange={(e) => {
                    setArea(e.target.value);
                    setFormDirty(true);
                  }}
                />
              </div>
              <div className="group">
                <label htmlFor="divisa">Divisa</label>
                <select
                  id="divisa"
                  name="divisa"
                  value={divisa}
                  onChange={(e) => {
                    setDivisa(e.target.value);
                    setFormDirty(true); // Marcar el formulario como modificado
                  }}
                >
                  <option value="MXN">MXN</option>
                  <option value="USD">USD</option>
                </select>
              </div>
            </div>

            <br />

            <div className="group">
              <label htmlFor="asunto">Asunto</label>
              <input
                type="text"
                id="asunto"
                name="asunto"
                {...SPELL}
                value={asunto}
                onChange={(e) => {
                  setAsunto(e.target.value);
                  setFormDirty(true);
                }}
                maxLength={46}
              />
            </div>

            <br />

            <div className="apartado2">Tabla de productos</div>

            <div>
              {/* Contenedor para la tabla y los botones */}
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  width: "100%",
                }}
              >
                {/* Tabla general */}

                <div>
                  <table className="productos-table">
                    <thead>
                      <tr>
                        <th>Detalles del Artículo</th>
                        <th style={{ textAlign: "center" }}>Modelo</th>
                        <th id="cantidad-col">Cantidad</th>
                        <th id="precio-col">Precio</th>
                        <th id="descuento-col">Descuento</th>
                        <th id="importe-col">Importe</th>
                      </tr>
                    </thead>
                    <tbody>
                      {datos.map((producto) => {
                        const tituloUI = producto.titulo || "";
                        const cuerpoUI = producto.descripcion || "";

                        return (
                          <tr
                            key={producto.id}
                            style={{ position: "relative" }}
                          >
                            {/* Detalles del Artículo */}
                            <td>
                              {/* TÍTULO (controla SOLO "titulo") */}
                              <input
                                type="text"
                                {...SPELL}
                                value={tituloUI}
                                placeholder="Título del artículo"
                                onChange={(e) => {
                                  const nuevoTitulo = e.target.value;
                                  setDatos((prev) =>
                                    prev.map((item) =>
                                      item.id === producto.id
                                        ? { ...item, titulo: nuevoTitulo }
                                        : item
                                    )
                                  );
                                  setFormDirty(true);
                                }}
                                onBlur={(e) => {
                                  const t = (e.target.value || "")
                                    .trim()
                                    .toUpperCase();
                                  setDatos((prev) =>
                                    prev.map((item) =>
                                      item.id === producto.id
                                        ? { ...item, titulo: t }
                                        : item
                                    )
                                  );
                                }}
                                style={{
                                  width: "600px",
                                  padding: "8px",
                                  fontSize: "14px",
                                  marginBottom: "6px",
                                  fontWeight: "bold",
                                }}
                              />

                              {/* DESCRIPCIÓN (controla SOLO "descripcion") */}
                              <div style={{ position: "relative" }}>
                                <textarea
                                  className="list-products"
                                  {...SPELL}
                                  value={cuerpoUI}
                                  onChange={(e) => {
                                    const body = e.target.value;
                                    // 1) Actualiza el cuerpo en tu estado controlado
                                    setDatos((prev) =>
                                      prev.map((item) =>
                                        item.id === producto.id
                                          ? { ...item, descripcion: body }
                                          : item
                                      )
                                    );
                                    // 2) Mantén tu autocompletar funcionando (usa tu hook)
                                    handleInputChange(e, producto.id);
                                    setFormDirty(true);
                                  }}
                                  onBlur={() => {
                                    // Cierra sugerencias sin tocar título/descripcion
                                    setTimeout(
                                      () => setFilteredProductos([]),
                                      100
                                    );
                                  }}
                                  placeholder="Descripción (sin el título)"
                                  style={{
                                    width: "600px",
                                    height: "130px",
                                    padding: "8px",
                                    fontSize: "14px",
                                    lineHeight: "1.5",
                                    resize: "vertical",
                                    overflow: "auto",
                                    whiteSpace: "pre-wrap",
                                    fontFamily: "Arial, sans-serif",
                                  }}
                                />

                                {/* Autocompletar */}
                                {activeRow === producto.id &&
                                  filteredProductos.length > 0 && (
                                    <ul
                                      style={{
                                        position: "absolute",
                                        top: "105%",
                                        left: 0,
                                        width: "100%",
                                        backgroundColor: "#fff",
                                        border: "1px solid #cecece",
                                        borderRadius: "5px",
                                        zIndex: 10,
                                        maxHeight: "150px",
                                        overflowY: "auto",
                                        listStyle: "none",
                                        padding: "5px",
                                        margin: 0,
                                      }}
                                    >
                                      {filteredProductos.map((prod) => (
                                        <li
                                          key={prod.id}
                                          onMouseDown={() => {
                                            // Tomamos el cuerpo para la descripción SIN tocar el título
                                            const sNombre = String(
                                              prod.nombre || ""
                                            ).replace(/\r/g, "");
                                            const [, ...rest] =
                                              sNombre.split("\n"); // quitamos la 1ª línea (posible título)
                                            const fromNombre = rest
                                              .join("\n")
                                              .trim();

                                            // Si tu catálogo trae 'descripcion', úsala; si no, usa el cuerpo derivado; si tampoco hay, usa el nombre completo.
                                            const body =
                                              String(
                                                prod.descripcion || ""
                                              ).trim() ||
                                              fromNombre ||
                                              sNombre;

                                            setDatos((prev) =>
                                              prev.map((item) =>
                                                item.id === producto.id
                                                  ? {
                                                      ...item,
                                                      descripcion: body, // <-- SOLO descripcion
                                                      // titulo: item.titulo, // (se respeta tal cual)
                                                    }
                                                  : item
                                              )
                                            );
                                            setFilteredProductos([]);
                                            setFormDirty(true);
                                          }}
                                          style={{
                                            padding: "5px",
                                            cursor: "pointer",
                                            backgroundColor: "#f9f9f9",
                                            marginBottom: "2px",
                                          }}
                                        >
                                          {prod.nombre}
                                        </li>
                                      ))}
                                    </ul>
                                  )}
                              </div>
                            </td>

                            {/* Modelo */}
                            <td>
                              <input
                                type="text"
                                {...SPELL}
                                value={
                                  producto.modelo ||
                                  producto.numeroDeParteCliente ||
                                  ""
                                }
                                onChange={(e) => {
                                  const modelo = e.target.value;
                                  setDatos((prev) =>
                                    prev.map((item) =>
                                      item.id === producto.id
                                        ? { ...item, modelo }
                                        : item
                                    )
                                  );
                                  setFormDirty(true);
                                }}
                              />
                            </td>

                            {/* Cantidad */}
                            <td id="cantidad-col">
                              <input
                                type="text"
                                inputMode="numeric"
                                pattern="[0-9]*"
                                value={(producto.cantidad ?? "").toString()}
                                onChange={(e) => {
                                  const v = e.target.value;

                                  // Solo permitimos dígitos (o vacío mientras escribe)
                                  if (!/^\d*$/.test(v)) return;

                                  const qty = v === "" ? "" : parseInt(v, 10);

                                  setDatos((prev) =>
                                    prev.map((item) =>
                                      item.id === producto.id
                                        ? {
                                            ...item,
                                            cantidad: qty,
                                            importe:
                                              (item.tarifa || 0) * (qty || 0),
                                          }
                                        : item
                                    )
                                  );
                                  setFormDirty(true);
                                }}
                                onBlur={(e) => {
                                  // Si quedó vacío al salir, lo normalizamos a 0
                                  const v = e.target.value;
                                  const qty = v === "" ? 0 : parseInt(v, 10);
                                  setDatos((prev) =>
                                    prev.map((item) =>
                                      item.id === producto.id
                                        ? {
                                            ...item,
                                            cantidad: qty,
                                            importe: (item.tarifa || 0) * qty,
                                          }
                                        : item
                                    )
                                  );
                                }}
                                style={{
                                  width: "100%",
                                  textAlign: "right",
                                  padding: "8px",
                                  fontSize: "14px",
                                  borderRadius: "4px",
                                }}
                              />
                            </td>

                            {/* Precio */}
                            <td id="precio-col">
                              <input
                                type="text"
                                inputMode="decimal"
                                value={(producto.tarifa ?? "").toString()}
                                onChange={(e) => {
                                  const raw = e.target.value.replace(",", "."); // acepta . o ,
                                  // permite vacío, "0.", ".5", "10.3", etc.
                                  if (
                                    !/^(\d+(\.\d*)?|\.\d*)?$/.test(raw) &&
                                    raw !== ""
                                  )
                                    return;

                                  setDatos((prev) =>
                                    prev.map((item) =>
                                      item.id === producto.id
                                        ? {
                                            ...item,
                                            // guardamos el string mientras escribe para no perder el punto
                                            tarifa: raw,
                                            // calculamos importe con lo que haya (si está vacío -> 0)
                                            importe:
                                              raw === ""
                                                ? 0
                                                : (parseFloat(raw) || 0) *
                                                  (item.cantidad || 0),
                                          }
                                        : item
                                    )
                                  );
                                  setFormDirty(true);
                                }}
                                onBlur={(e) => {
                                  // normalizamos a número al salir del campo
                                  const num =
                                    parseFloat(
                                      e.target.value.replace(",", ".")
                                    ) || 0;
                                  setDatos((prev) =>
                                    prev.map((item) =>
                                      item.id === producto.id
                                        ? {
                                            ...item,
                                            tarifa: num,
                                            importe: num * (item.cantidad || 0),
                                          }
                                        : item
                                    )
                                  );
                                }}
                                style={{
                                  width: "100%",
                                  textAlign: "right",
                                  padding: "8px",
                                  fontSize: "14px",
                                  borderRadius: "4px",
                                }}
                              />
                            </td>

                            {/* Descuento */}
                            <td id="descuento-col">
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  height: "32px",
                                  border: "1px solid #ccc",
                                  borderRadius: "4px",
                                  overflow: "hidden",
                                  width: "100%",
                                }}
                              >
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  value={(producto.descuento ?? "").toString()}
                                  onChange={(e) => {
                                    const raw = e.target.value.replace(
                                      ",",
                                      "."
                                    ); // permite coma o punto
                                    // permite vacío, ".5", "0.", "10.5", etc.
                                    if (!/^(\d+(\.\d*)?|\.\d*)?$/.test(raw))
                                      return;

                                    setDatos((prev) =>
                                      prev.map((item) => {
                                        if (item.id !== producto.id)
                                          return item;

                                        const qty = Number(item.cantidad) || 0;
                                        const price =
                                          parseFloat(item.tarifa) || 0;
                                        const tipo = item.tipoDescuento || "%";
                                        const dnum =
                                          raw === "" || raw === "."
                                            ? 0
                                            : Math.max(parseFloat(raw) || 0, 0);

                                        const importe =
                                          tipo === "%"
                                            ? price * qty * (1 - dnum / 100)
                                            : Math.max(price * qty - dnum, 0);

                                        return {
                                          ...item,
                                          descuento: raw,
                                          importe,
                                        };
                                      })
                                    );
                                    setFormDirty(true);
                                  }}
                                  onBlur={(e) => {
                                    let d =
                                      parseFloat(
                                        e.target.value.replace(",", ".")
                                      ) || 0;
                                    if (
                                      producto.tipoDescuento === "%" &&
                                      d > 100
                                    )
                                      d = 100;
                                    if (d < 0) d = 0;

                                    setDatos((prev) =>
                                      prev.map((item) => {
                                        if (item.id !== producto.id)
                                          return item;
                                        const qty = Number(item.cantidad) || 0;
                                        const price =
                                          parseFloat(item.tarifa) || 0;
                                        const tipo = item.tipoDescuento || "%";
                                        const importe =
                                          tipo === "%"
                                            ? price * qty * (1 - d / 100)
                                            : Math.max(price * qty - d, 0);
                                        return {
                                          ...item,
                                          descuento: d,
                                          importe,
                                        };
                                      })
                                    );
                                  }}
                                  style={{
                                    flex: 1,
                                    border: "none",
                                    outline: "none",
                                    textAlign: "right",
                                    padding: "0 12px",
                                    fontSize: "16px",
                                    lineHeight: "32px",
                                  }}
                                />
                                <div
                                  style={{
                                    position: "relative",
                                    width: "50px",
                                    height: "100%",
                                  }}
                                >
                                  <select
                                    value={producto.tipoDescuento || "%"}
                                    onChange={(e) => {
                                      const tipo = e.target.value;
                                      setDatos((prev) =>
                                        prev.map((item) => {
                                          if (item.id !== producto.id)
                                            return item;
                                          const qty =
                                            Number(item.cantidad) || 0;
                                          const price =
                                            parseFloat(item.tarifa) || 0;
                                          const d =
                                            parseFloat(
                                              String(item.descuento).replace(
                                                ",",
                                                "."
                                              )
                                            ) || 0;

                                          const importe =
                                            tipo === "%"
                                              ? price * qty * (1 - d / 100)
                                              : Math.max(price * qty - d, 0);

                                          return {
                                            ...item,
                                            tipoDescuento: tipo,
                                            importe,
                                          };
                                        })
                                      );
                                      setFormDirty(true);
                                    }}
                                    style={{
                                      appearance: "none",
                                      WebkitAppearance: "none",
                                      MozAppearance: "none",
                                      width: "100%",
                                      height: "100%",
                                      border: "none",
                                      outline: "none",
                                      padding: "0 12px",
                                      fontSize: "16px",
                                      lineHeight: "32px",
                                      backgroundColor: "#f4f4f4",
                                      textAlign: "center",
                                      cursor: "pointer",
                                    }}
                                  >
                                    <option value="%">%</option>
                                    <option value="$">$</option>
                                  </select>
                                  <i
                                    style={{
                                      position: "absolute",
                                      right: "8px",
                                      top: "50%",
                                      transform: "translateY(-50%)",
                                      pointerEvents: "none",
                                      color: "#666",
                                      fontSize: "12px",
                                    }}
                                  />
                                </div>
                              </div>
                            </td>

                            {/* Importe */}
                            <td
                              id="importe-col"
                              style={{
                                textAlign: "right",
                                paddingRight: "10px",
                              }}
                            >
                              $
                              {parseFloat(producto.importe || 0).toLocaleString(
                                "en-US",
                                {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                }
                              )}
                            </td>

                            {/* Botones de acción */}
                            <div
                              style={{
                                position: "absolute",
                                right: "-75px",
                                top: "50%",
                                transform: "translateY(-50%)",
                                display: "flex",
                              }}
                            >
                              <button
                                type="button"
                                style={{
                                  background: "none",
                                  border: "none",
                                  cursor: "pointer",
                                }}
                              >
                                <i
                                  className="bi bi-three-dots-vertical"
                                  style={{ fontSize: "1.2rem" }}
                                />
                              </button>
                              <button
                                type="button"
                                style={{
                                  background: "none",
                                  border: "none",
                                  color: "#dc3545",
                                  cursor: "pointer",
                                }}
                                onClick={() => eliminar(producto.id)}
                              >
                                <i
                                  className="bi bi-x-lg"
                                  style={{ fontSize: "1.2rem" }}
                                />
                              </button>
                            </div>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="acciones-tabla">
              <button type="button" onClick={añadirFila}>
                <i
                  style={{ color: "#007bff", fontSize: "16px" }}
                  className="bi bi-plus-circle-fill"
                ></i>
                Añadir nueva fila
              </button>
              <button type="button" onClick={() => agregarBulk(datos)}>
                <i
                  style={{ color: "#007bff", fontSize: "16px" }}
                  className="bi bi-plus-circle-fill"
                ></i>
                Agregar artículos a granel
              </button>
            </div>

            <div
              className="data-container"
              style={{
                display: "flex",
                justifyContent: "flex-end",
                paddingRight: "80px",
              }}
            >
              <div className="totales-container">
                {/* Cálculos actualizados */}
                {(() => {
                  const subtotal = calcularSubtotal();
                  const ivaRate = obtenerIVA();
                  const descuentoAplicado =
                    tipoDescuentoGlobal === "%"
                      ? subtotal * (descuento / 100)
                      : Math.min(descuento, subtotal);
                  const subtotalConDesc = subtotal - descuentoAplicado;
                  const ivaTotal = subtotalConDesc * ivaRate;
                  const totalFinal = subtotalConDesc + ivaTotal;

                  return (
                    <>
                      {/* Subtotal */}
                      <div className="row">
                        <span className="label">Subtotal</span>
                        <span>
                          $
                          {subtotal.toLocaleString("en-US", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </span>
                      </div>

                      {/* Descuento global */}
                      <div className="row descuento">
                        <span className="label">Descuento</span>

                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            border: "1px solid #ddd",
                            borderRadius: "8px",
                            overflow: "visible",
                            height: 40,
                            minWidth: 180,
                            background: "#fff",
                          }}
                        >
                          <input
                            type="text"
                            inputMode="decimal"
                            value={(descuento ?? "").toString()}
                            onChange={(e) => {
                              const raw = e.target.value.replace(",", ".");
                              if (!/^(\d+(\.\d*)?|\.\d*)?$/.test(raw)) return;
                              setDescuento(raw === "" ? "" : raw);
                              setFormDirty(true);
                            }}
                            onBlur={(e) => {
                              let d =
                                parseFloat(e.target.value.replace(",", ".")) ||
                                0;
                              if (tipoDescuentoGlobal === "%" && d > 100)
                                d = 100;
                              if (
                                tipoDescuentoGlobal === "MXN" &&
                                d > calcularSubtotal()
                              )
                                d = calcularSubtotal();
                              if (d < 0) d = 0;
                              setDescuento(d);
                            }}
                            style={{
                              flex: 1,
                              border: "none",
                              outline: "none",
                              textAlign: "right",
                              padding: "0 12px",
                              fontSize: "16px",
                              lineHeight: "32px",
                              background: "transparent",
                            }}
                          />

                          <MiniSelect
                            value={tipoDescuentoGlobal}
                            options={[
                              { value: "%", label: "%" },
                              { value: "MXN", label: "MXN" },
                            ]}
                            onChange={(next) => {
                              setTipoDescuentoGlobal(next);
                              setDescuento((prev) => {
                                let d = parseFloat(prev) || 0;
                                if (next === "%" && d > 100) d = 100;
                                if (next === "MXN" && d > calcularSubtotal())
                                  d = calcularSubtotal();
                                if (d < 0) d = 0;
                                return d;
                              });
                            }}
                          />
                        </div>

                        <span className="discount-percentage">
                          – $
                          {(tipoDescuentoGlobal === "%"
                            ? calcularSubtotal() *
                              ((parseFloat(descuento) || 0) / 100)
                            : Math.min(
                                parseFloat(descuento) || 0,
                                calcularSubtotal()
                              )
                          ).toLocaleString("en-US", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </span>
                      </div>

                      {/* IVA */}
                      <div className="row iva">
                        <span className="label">
                          IVA ({(ivaRate * 100).toFixed(0)}%)
                        </span>
                        <span>
                          $
                          {ivaTotal.toLocaleString("en-US", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </span>
                      </div>

                      <hr />

                      {/* Total */}
                      <div className="row total">
                        <span>Total ({divisa})</span>
                        <span className="total-amount">
                          $
                          {totalFinal.toLocaleString("en-US", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </span>
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>

            <div
              style={{
                backgroundColor: "#f9f9fb",
                padding: "8px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: "10px",
                width: "94%",
                marginTop: "30px",
                paddingBottom: "30px",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
              }}
            >
              {/* Left Section: Términos y Condiciones */}
              <div className="terminos-container">
                <h4>Tiempo de entrega*</h4>
                <textarea
                  id="tiempoEntrega"
                  name="tiempoEntrega"
                  {...SPELL}
                  required
                  value={tiempoEntrega}
                  onChange={(e) => {
                    setTiempoEntrega(e.target.value);
                    setFormDirty(true);
                  }}
                  placeholder="Ejemplo: 1 semana después de confirmar con OC"
                  rows={3} // <— altura inicial
                  style={{
                    width: "100%",
                    /* minHeight: "100px",  <-- quítalo o reduce */
                    minHeight: "60px", // <— altura más compacta por defecto
                    fontSize: "14px",
                    lineHeight: "1.5",
                    padding: "8px",
                    resize: "vertical",
                    overflow: "auto",
                    fontFamily: "Arial, sans-serif",
                  }}
                />

                <h4 style={{ marginTop: "20px" }}>Condiciones de pago*</h4>
                <textarea
                  id="condicionesPago"
                  name="condicionesPago"
                  {...SPELL}
                  required
                  value={condicionesPago}
                  onChange={(e) => {
                    setCondicionesPago(e.target.value);
                    setFormDirty(true);
                  }}
                  placeholder="Ejemplo: 50% anticipo y 50% contra entrega"
                  rows={3} // <— altura inicial
                  style={{
                    width: "100%",
                    /* minHeight: "100px",  <-- quítalo o reduce */
                    minHeight: "60px", // <— altura más compacta por defecto
                    fontSize: "14px",
                    lineHeight: "1.5",
                    padding: "8px",
                    resize: "vertical",
                    overflow: "auto",
                    fontFamily: "Arial, sans-serif",
                  }}
                />
              </div>

              {/* Right Section: File Upload */}
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  gap: "8px",
                  flex: "0 0 530px",
                  marginTop: "30px",
                }}
              >
                <label style={{ fontSize: "14px" }}>
                  Adjuntar archivo(s) a Estimación
                </label>

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    border: "1px dashed #ccc",
                    borderRadius: "5px",
                    overflow: "hidden", // Eliminar cualquier contenido que se salga del borde
                  }}
                >
                  <label
                    style={{
                      padding: "8px 10px",
                      backgroundColor: "#fff",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      cursor: "pointer",
                      whiteSpace: "nowrap", // Evitar que el contenido del botón se ajuste en varias líneas
                    }}
                  >
                    <i style={{ color: "#000" }} className="bi bi-upload"></i>
                    <span
                      style={{
                        fontSize: "14px",
                        color: "#000",
                        fontWeight: "lighter",
                      }}
                    >
                      Cargar archivo
                    </span>
                    <input
                      type="file"
                      style={{ display: "none" }} // Ocultar el input de archivo
                      accept=".csv,.xls,.xlsx,.doc,.docx,.pdf,.jpg,.jpeg,.png,.svg,.txt"
                      onChange={manejarArchivos}
                    />
                  </label>

                  <div
                    style={{
                      padding: "10px",
                      backgroundColor: "#fff",
                      borderLeft: "1px dashed #ccc", // Borde que conecta con el input
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      borderTopRightRadius: "auto", // Bordes redondeados en la esquina derecha
                      borderBottomRightRadius: "auto",
                      minWidth: "20px", // Evitar que el botón se haga demasiado pequeño
                      margin: "0", // Eliminar cualquier margen que agregue espacio extra
                    }}
                  >
                    <i
                      style={{
                        color: "#000",
                        fontSize: "14px", // Ajustar el tamaño del ícono
                      }}
                      className="bi bi-chevron-down"
                    ></i>
                  </div>
                </div>

                <p style={{ fontSize: "12px", color: "#888" }}>
                  Puedes cargar un máximo de 10 archivos, cada uno de 10 MB
                </p>

                {cargando && (
                  <p style={{ fontSize: "14px", color: "#888" }}>
                    Cargando archivo...
                  </p>
                )}

                {archivos.length > 0 && (
                  <div style={{ width: "100%" }}>
                    <strong>Archivos cargados:</strong>
                    <ul
                      style={{
                        listStyle: "none",
                        padding: 0,
                        marginTop: "10px",
                        display: "flex",
                        flexDirection: "column",
                        gap: "10px",
                      }}
                    >
                      {archivos.map((archivo, index) => {
                        const extension = archivo.nombre
                          .split(".")
                          .pop()
                          .toLowerCase();
                        const { icon, color } = obtenerIcono(extension);

                        return (
                          <li
                            key={index}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "5px",
                              padding: "10px",
                              border: "1px solid #ddd",
                              borderRadius: "5px",
                              boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                              backgroundColor: "#f9ffb",
                            }}
                          >
                            <i
                              className={`bi ${icon}`}
                              style={{
                                color,
                                fontSize: "1.5rem",
                                flexShrink: 0,
                              }}
                            />
                            <span style={{ flexGrow: 1 }}>
                              {archivo.nombre}
                            </span>

                            {/* Descargar */}
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                descargarArchivo(archivo.url, archivo.nombre);
                              }}
                              style={{
                                color: "#007bff",
                                background: "none",
                                border: "none",
                                fontWeight: "bold",
                                cursor: "pointer",
                              }}
                            >
                              <i
                                className="bi bi-download"
                                style={{ fontSize: "20px" }}
                              />
                            </button>

                            {/* Eliminar */}
                            <button
                              type="button"
                              onClick={() => eliminarArchivo(archivo.nombre)}
                              style={{
                                border: "none",
                                background: "transparent",
                                color: "#dc3545",
                                cursor: "pointer",
                              }}
                            >
                              <i
                                className="bi bi-trash"
                                style={{ fontSize: "20px" }}
                              />
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Grupo de botones: Guardar como borrador, Guardar y enviar, Cancelar */}
          <div className="cotizacion-toolbar">
            <div className="btn-group">
              <button
                id="guardar-enviar"
                className="btn btn-primary"
                type="button"
                onClick={guardar}
                disabled={isSaving}
              >
                {isSaving ? "Guardando…" : "Guardar y enviar"}
              </button>

              <button
                id="cancelar"
                className="btn btn-secondary"
                type="button"
                onClick={handleCancel}
              >
                Cancelar
              </button>
            </div>
          </div>
        </form>

        {mostrarTabla && (
          <TablaCotizaciones
            numeroCotizacion={numeroSobrescrito ?? numeroCotizacionAuto}
            cotizaciones={cotizaciones}
          />
        )}
      </div>

      <NumeroConfigModalUI />
    </>
  );
}

export default CotizacionForm;
